import invariant from 'invariant'
import _ from 'lodash'
import { RecaptchaV2Challenge } from './v2Challenge'
import React from 'react'
import ReactDOM from 'react-dom'
import { IntlProvider } from 'react-intl'

// This should not be used directly, use the factory method in the module index file
export class RecaptchaRunner {
  _config = null
  _token = null
  _scriptReady = false
  _isV2Running = false
  _v2RequestQueue = []

  constructor (config) {
    this._config = config
    this._init()
  }

  /* Public method */
  /* execute a function under the protection of recaptcha */
  /* returns whatever fn is evaluated to */
  async run (fn, action) {
    this._token = await this._v3Execute(action)
    let result = await this._runProtected(fn)

    if (_.get(result, 'data.isRecaptchaOk')) {
      return result
    }
    const config = _.get(result, 'data', {})
    // At this point, there might be multiple v2 requests
    // the first successful request will remove the rendered recaptcha page
    // we have to do some congestion control
    if (!this._isV2Running) {
      // it's ok to take config from first request. they should be the same, anyway
      this._v2Execute(config).then(token => {
        this._token = token
        let req
        while ((req = this._v2RequestQueue.shift())) req()
        this._isV2Running = false
      })
      this._isV2Running = true
    }

    return new Promise((resolve, reject) => {
      this._v2RequestQueue.push(() => {
        resolve(this._runProtected(fn))
      })
    })
  }

  _init () {
    let script = document.createElement('script')
    script.setAttribute('src', this._config.v3.url + this._config.v3.siteKey)
    document.head.appendChild(script)

    script.onload = () => {
      grecaptcha.ready(this._onScriptReady.bind(this))
    }
  }

  _onScriptReady () {
    this._scriptReady = true
  }

  /* get v3 token */
  async _v3Execute (action) {
    if (!this._scriptReady) {
      await this._condition(() => this._scriptReady)
    }
    this.version = 'v3'
    return grecaptcha.execute(this._config.v3.siteKey, { action })
  }

  /* get v2 token */
  async _v2Execute (config) {
    if (!this._scriptReady) {
      await this._condition(() => this._scriptReady)
    }
    this.version = 'v2'
    return new Promise((resolve, reject) => {
      const cb = token => {
        this._token = token
        resolve(token)
      }

      this._renderV2ChallengePage(config, cb, reject)
    })
  }

  _renderV2ChallengePage (renderProps, successCb, failCb) {
    const { messages, locale, v2 } = this._config
    const ip = _.get(renderProps, 'ip', '')
    let el = document.querySelector('#recaptcha-challenge')
    if (!el) {
      el = document.createElement('div')
      el.setAttribute('id', 'recaptcha-challenge')
      document.body.appendChild(el)
    }
    const injectedSuccessCb = (...args) => {
      successCb(...args)
      render(false)
    }

    const injectedFailCb = (...args) => {
      failCb(...args)
      render(false)
    }

    function render (show) {
      ReactDOM.render(
        <div>
          {show && (
            <IntlProvider messages={messages} locale={locale} defaultLocale={'en'}>
              <RecaptchaV2Challenge
                siteKey={v2.siteKey}
                onSuccess={injectedSuccessCb}
                onFail={injectedFailCb}
                ip={ip}
              />
            </IntlProvider>
          )}
        </div>,
        el
      )
    }
    render(true)
  }

  async _runProtected (fn) {
    invariant(this._token, 'Recaptcha token is null')
    return fn(this.version + '-' + this._token)
  }

  async _condition (check) {
    if (!check()) {
      await new Promise(resolve => setTimeout(resolve, 200))
      return this._condition(check)
    }
  }
}
